import React from 'react';

import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import Icon from '@/client/components/ui/icon/Icon';
import { faqItems, type FAQItem, type FAQLink } from '@/client/catalogs/landing/faq';
import { track } from '@/client/pages/landing/lib/analytics';
import { cn } from '@/client/pages/landing/lib/cn';
import FaqAnswer from './FaqAnswer';

type FAQSurface = 'plain' | 'panel';

type FAQProps = {
	id?: string;
	eyebrow?: string;
	title?: string;
	description?: string;
	items?: readonly FAQItem[];
	sectionClassName?: string;
	surface?: FAQSurface;
	contactHref?: string;
	contactLabel?: string;

	/**
	 * Extra content for the heading column: a glossary, a rules card, a side CTA.
	 * The 2026-09 mockups fill that column on pages whose FAQ is short, where the
	 * heading alone would leave a tall empty rail beside the answers. It is a grid
	 * sibling placed under the heading from `lg`, so on a phone it follows the answers
	 * in document order, which is where the phone mockups put it, with no second copy.
	 */
	aside?: React.ReactNode;

	/** Pages that track their own CTAs pass their tracker; the link works either way. */
	onContactClick?: () => void;
	showItemLinks?: boolean;
	isItemLinkVisible?: (link: FAQLink) => boolean;

	/**
	 * Opens every link in this section in a new tab.
	 *
	 * For `/pricing`, where a visitor who opens the refund policy or the privacy
	 * page is mid-decision: sending them away in the same tab loses the price,
	 * the plan card and whatever conversion context the URL was carrying.
	 */
	openLinksInNewTab?: boolean;
};

// Gutters match PAGE_CONTAINER_CLASS (20px on a phone, 32px from lg), so the FAQ well
// lines up with the cards of the page around it.
const DEFAULT_SECTION_CLASS =
	'mx-auto max-w-[1240px] scroll-mt-[calc(var(--ud-topbar-h,60px)+24px)] px-5 py-16 lg:px-8 lg:py-24';

/**
 * The rhythm every page's FAQ sits on.
 *
 * One constant rather than a string repeated at each call site: the section is
 * the same block everywhere, so its spacing is not a per-page decision.
 */
export const FAQ_SECTION_CLASS = 'pb-0 pt-24 lg:pt-[110px]';

/**
 * The same block on a page of the 2026-09 redesign, where every section opens with
 * the page rhythm (56px on a phone, 112px on desktop) and the section after the FAQ
 * brings its own top spacing. `lg:pb-0` is spelled out because the default class
 * carries `lg:py-24`, which a bare `pb-0` cannot override at that breakpoint.
 */
export const FAQ_FLUSH_SECTION_CLASS = 'pb-0 pt-14 lg:pb-0 lg:pt-28';

/**
 * What the two surfaces actually differ on.
 *
 * They used to be two near-identical JSX trees, which meant every change to the
 * accordion had to be made twice and the topic labels would have been a third
 * copy. The differences are four class strings and the marker glyph, so those
 * are the only things that branch.
 */
const SURFACE_PRESENTATION: Record<FAQSurface, {
	listWrapperClass: string;
	listClass: string;
	itemClass: string;
	renderMarker: () => React.ReactNode;
}> = {
	plain: {
		listWrapperClass: '',
		listClass: 'flex min-w-0 flex-col gap-3',
		itemClass: 'group rounded-[20px] bg-[var(--color-card)] px-[26px] py-[22px]',
		renderMarker: () => (
			<span class="flex-none text-[20px] font-normal leading-none text-slate-400 transition-transform duration-200 group-open:rotate-45">
				+
			</span>
		),
	},
	panel: {
		listWrapperClass: 'rounded-[30px] bg-[#f1ede7] p-2.5 sm:p-3',
		listClass: 'flex min-w-0 flex-col gap-2.5 sm:gap-3',
		itemClass:
			'group rounded-[22px] bg-[#f9f8f6] px-[26px] py-[22px] transition-[background-color,box-shadow] duration-200 open:bg-white open:shadow-[0_14px_28px_rgba(27,29,30,0.11)]',
		renderMarker: () => (
			<Icon
				name="plus"
				size={20}
				strokeWidth={1.5}
				className="flex-none text-[#a7a9a8] transition-transform duration-200 group-open:rotate-45"
			/>
		),
	},
};

export function FAQ({
	id = 'faq',
	title = 'The practical questions people ask before subscribing.',
	description,
	items = faqItems,
	sectionClassName,
	surface = 'plain',
	contactHref = '/team',
	contactLabel = 'Contact me',
	aside,
	onContactClick,
	showItemLinks = false,
	isItemLinkVisible,
	openLinksInNewTab = false,
}: FAQProps) {
	const handleToggle = React.useCallback((event: React.TargetedEvent<HTMLDetailsElement>) => {
		if (typeof window === 'undefined') return;
		const detailsElement = event.currentTarget;
		if (!detailsElement.open) return;

		const question = detailsElement.getAttribute('data-question');
		if (question) {
			track('faq_expand', { question });
		}
	}, []);

	const presentation = SURFACE_PRESENTATION[surface];
	const newTabProps = openLinksInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};

	return (
		<section id={id} class={cn(DEFAULT_SECTION_CLASS, sectionClassName)}>
			<div class="grid grid-cols-1 items-start gap-x-14 gap-y-10 lg:grid-cols-2">
				{/* With an aside the heading shares its column, so it stops being sticky: a
				    stuck heading would slide over the card below it. */}
				<div data-reveal class={aside ? undefined : 'lg:sticky lg:top-[110px]'}>
					<h2 class="text-balance text-[2.125rem] font-medium leading-[1.1] tracking-[-0.03em] text-slate-950 sm:text-[2.875rem]">
						{title}
					</h2>
					{description ? (
						<p class="mt-5 max-w-[420px] text-[16.5px] leading-[1.65] text-slate-600">{description}</p>
					) : null}
					<div class="mt-7">
						<CtaPill
							href={contactHref}
							variant="light"
							onClick={onContactClick}
							{...newTabProps}
						>
							{contactLabel}
						</CtaPill>
					</div>
				</div>

				<div
					data-reveal
					class={cn(presentation.listWrapperClass, aside ? 'lg:col-start-2 lg:row-span-2 lg:row-start-1' : undefined)}
				>
					<div class={presentation.listClass}>
						{items.map((item, index) => {
							/* A label opens each run of one topic, so a 29-answer list reads
							   as four short ones. Pages that pass topic-free items (compare,
							   product, solutions) render exactly what they did before. */
							const previousTopic = index === 0 ? undefined : items[index - 1].topic;
							const startsTopic = Boolean(item.topic) && item.topic !== previousTopic;

							return (
								<React.Fragment key={item.question}>
									{startsTopic ? (
										<p
											class={cn(
												'px-[26px] pb-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-600',
												index === 0 ? 'pt-1' : 'pt-5',
											)}
										>
											{item.topic}
										</p>
									) : null}

									<details
										data-question={item.question}
										onToggle={handleToggle}
										class={presentation.itemClass}
									>
										<summary class="flex cursor-pointer list-none items-center justify-between gap-4 text-[16px] font-semibold tracking-[-0.01em] text-slate-950 marker:hidden">
											<span>{item.question}</span>
											{presentation.renderMarker()}
										</summary>
										<FaqAnswer
											item={item}
											showLink={showItemLinks && (!item.link || !isItemLinkVisible || isItemLinkVisible(item.link))}
											openInNewTab={openLinksInNewTab}
										/>
									</details>
								</React.Fragment>
							);
						})}
					</div>
				</div>

				{aside ? (
					<div data-reveal class="lg:col-start-1 lg:row-start-2">
						{aside}
					</div>
				) : null}
			</div>
		</section>
	);
}
